import React, { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  Input,
  InputGroup,
  FormControl,
  FormErrorMessage,
  FormLabel,
  FormHelperText,
  Box,
} from '@chakra-ui/react';
import { cx } from '@chakra-ui/utils';
import formatStringByPattern from 'format-string-by-pattern';

import { InputControlProps } from '../../../@types';
import { createLogLocal } from '../../../utils/createLogLocal';

const log = createLogLocal('InputControl.tsx');

const formatInput = (format: string, cardNumber: string) =>
  formatStringByPattern(format, cardNumber.replace(/\D/g, ''));

export const InputControl = React.forwardRef(
  (
    {
      name: fieldName,
      label,
      helper,
      error,
      isDisabled = false,
      isRequired = false,
      validateOnBlur = false,
      validateOnChange = false,
      mask,
      rules,
      onChangeHandler,
      onBlurHandler,
      onErrorHandler,
      value,
      ...props
    }: InputControlProps,
    ref,
  ) => {
    const {
      register,
      getValues,
      setValue,
      trigger,
      formState: { errors },
    } = useFormContext();

    // Set value passed down via props
    useEffect(() => {
      if (value) setValue(fieldName, value);
    }, [value, setValue, fieldName]);

    const { t } = useTranslation();

    const registerOptions = {
      ...(isRequired && { required: t('requiredField') }),
      ...rules,
      onBlur: async () => {
        if (validateOnBlur) await trigger([fieldName]);
        if (onBlurHandler) onBlurHandler(fieldName as any);
      },
      onChange: async () => {
        if (mask) {
          const val = getValues(fieldName);
          setValue(fieldName, formatInput(mask, val));
        }
        if (validateOnChange) await trigger([fieldName]);
        if (onChangeHandler) onChangeHandler(fieldName as any);
        log(getValues(fieldName));
      },
    };

    const isInvalid = Boolean(errors[fieldName]?.message);

    React.useEffect(() => {
      if (
        (validateOnBlur || validateOnChange) &&
        onErrorHandler &&
        isInvalid &&
        errors[fieldName]?.message
      ) {
        onErrorHandler({
          [fieldName]: { ...errors[fieldName], key: fieldName },
        });
      }
    }, [
      fieldName,
      isInvalid,
      onErrorHandler,
      errors,
      validateOnBlur,
      validateOnChange,
    ]);

    const { ref: inputRef, ...rest } = register(fieldName, {
      ...registerOptions,
    });

    React.useImperativeHandle(ref, () => ({
      async beforeSubmit() {},
      async validate() {
        await trigger([fieldName]);
      },
    }));

    return (
      <FormControl
        isInvalid={isInvalid}
        isDisabled={isDisabled}
        isRequired={isRequired}
        sx={props.sx}
        // className={cx('InputContainer')}
      >
        <Box className={cx('InputContainer')}>
          <FormLabel className={cx('InputLabel')}>{label}</FormLabel>
          <InputGroup className={cx('Input')}>
            <Input {...props} {...rest} ref={inputRef} />
          </InputGroup>

          {!isInvalid && (
            <FormHelperText className={cx('InputHelper')}>
              {helper}
            </FormHelperText>
          )}
          {isInvalid && (
            <FormErrorMessage className={cx('InputError')}>
              {errors[fieldName]?.message as string}
            </FormErrorMessage>
          )}
        </Box>
      </FormControl>
    );
  },
) as any;
